import React, { useState, useEffect } from "react";
import UserService from "../services/user.service";
import EventBus from "../common/EventBus";
import { Button, Form, Spinner, Alert } from "react-bootstrap";
import ErrorModal from "./ErrorModal";
import { FaCheckCircle, FaTimesCircle, FaLock } from "react-icons/fa";

const BoardChangeMyPassword = () => {
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [apiErrors, setApiErrors] = useState(null);
  const [apiErrorCode, setApiErrorCode] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    UserService.getCustomerBoard()
      .then((response) => {
        const customer = response.data?.[0]?.t_customer || {};
        setEmail(customer.custumer_company_email || "");
      })
      .catch((error) => handleError(error))
      .finally(() => setLoading(false));
  }, []);

  const handleError = (error) => {
    if (error.response && error.response.status === 401) {
      EventBus.dispatch("logout");
    }
    if (error.response) {
      setApiErrors(`Fehler: ${error.response.data.message}`);
      setApiErrorCode(`Fehler: ${error.name} – ${error.response.status}`);
    } else if (error.request) {
      setApiErrors("Keine Antwort vom Server.");
      setApiErrorCode(`Fehler: ${error.name}`);
    } else {
      setApiErrors(`Fehler: ${error.message}`);
      setApiErrorCode(`Fehler: ${error.name}`);
    }
    setLoading(false);
    setSuccessMessage(null);
    setShowModal(true);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newPassword.trim()) {
      newErrors.newPassword = "Neues Passwort ist erforderlich";
    } else if (newPassword.length < 8) {
      newErrors.newPassword = "Das Passwort muss mindestens 8 Zeichen lang sein";
    } else if (!/[A-Z]/.test(newPassword)) {
      newErrors.newPassword = "Das Passwort muss mindestens einen Großbuchstaben enthalten";
    } else if (!/[a-z]/.test(newPassword)) {
      newErrors.newPassword = "Das Passwort muss mindestens einen Kleinbuchstaben enthalten";
    } else if (!/[0-9]/.test(newPassword)) {
      newErrors.newPassword = "Das Passwort muss mindestens eine Ziffer enthalten";
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      newErrors.newPassword = "Das Passwort muss mindestens ein Sonderzeichen enthalten";
    }
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Bitte das Passwort wiederholen";
    } else if (confirmPassword !== newPassword) {
      newErrors.confirmPassword = "Die Passwörter stimmen nicht überein";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    UserService.changeMyPasswordBoard({
      custumer_company_email: email,
      newPassword,
      confirmPassword,
    })
      .then(() => {
        setSuccessMessage("Das Passwort wurde geändert.");
        setApiErrors(null);
        setNewPassword("");
        setConfirmPassword("");
        setLoading(false);
      })
      .catch((error) => handleError(error));
  };

  const passwordRules = [
    { ok: newPassword.length >= 8, text: "Mindestens 8 Zeichen" },
    { ok: /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword), text: "Groß- und Kleinbuchstaben" },
    { ok: /[0-9]/.test(newPassword), text: "Mindestens eine Ziffer" },
    { ok: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword), text: "Mindestens ein Sonderzeichen" },
    { ok: confirmPassword !== "" && confirmPassword === newPassword, text: "Beide Eingaben stimmen überein" },
  ];

  return (
    <section className="myn-sheet">
      <header className="myn-sheet__head">
        <span className="myn-sheet__icon" aria-hidden="true"><FaLock /></span>
        <div>
          <p className="myn-sheet__kicker">Sicherheit</p>
          <h1 className="myn-sheet__title">Passwort ändern</h1>
          <p className="myn-sheet__lead">Hier ändern Sie nur Ihr Passwort. QR-Codes, Tarife und die Zieladresse der Bewertung liegen unter Meine QR-Codes. Ihre Stammdaten liegen unter Meine Daten.</p>
        </div>
      </header>
      <ErrorModal show={showModal} handleClose={() => setShowModal(false)} errorCode={apiErrorCode} errorMessage={apiErrors} />
      {apiErrors && <Alert variant="danger">{apiErrors}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}
      <div className="myn-sheet__panel">
        {loading ? (
          <p className="myn-sheet__muted"><Spinner animation="border" size="sm" /> Bitte warten …</p>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="formNewPassword">
              <Form.Label>Neues Passwort</Form.Label>
              <Form.Control type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} isInvalid={!!errors.newPassword} autoComplete="new-password" />
              <Form.Control.Feedback type="invalid">{errors.newPassword}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="formConfirmPassword" className="mt-3">
              <Form.Label>Passwort wiederholen</Form.Label>
              <Form.Control type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} isInvalid={!!errors.confirmPassword} autoComplete="new-password" />
              <Form.Control.Feedback type="invalid">{errors.confirmPassword}</Form.Control.Feedback>
            </Form.Group>
            <ul className="myn-sheet__rules">
              {passwordRules.map((rule) => (
                <li key={rule.text} className={rule.ok ? "is-ok" : ""}>
                  {rule.ok ? <FaCheckCircle aria-hidden="true" /> : <FaTimesCircle aria-hidden="true" />}
                  <span>{rule.text}</span>
                </li>
              ))}
            </ul>
            <Button className="myn-btn myn-btn--primary myn-btn--block" type="submit">Passwort speichern</Button>
          </Form>
        )}
      </div>
    </section>
  );
};

export default BoardChangeMyPassword;
